import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ResourcesUpdateStatus } from '@/api/DTO/resources/resourcesList.interface';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import Close from '@mui/icons-material/Close';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { CommunityResourcesType } from '@/api/enum/common.enum';
import { useUpdateResourcesStatusMutation } from '@/api/communityResources/resourcesListApiSlice';
import { useGetUser, useGetUserResultType } from '@/hooks/useGetUser';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

interface SettingStatusModalProps {
	currentStatus: string | number | undefined;
	setStatusButton?: boolean;
	selectedId: string[];
	setSelectedId?: any;
	resourcesStatusList: { id: string | number; name: string }[];
}

const SettingStatusModal = (props: SettingStatusModalProps) => {
	const {
		selectedId,
		setSelectedId,
		resourcesStatusList,
		setStatusButton,
		currentStatus,
	} = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [updateResourcesStatus] = useUpdateResourcesStatusMutation();
	const showModal = useModal();

	const { userRoles } = useGetUser<useGetUserResultType>({
		roleObj: {
			statusRemark: ['statusRemark', 'communityResources'],
		},
	});

	const defaultValues: DefaultValues<ResourcesUpdateStatus> = {
		mark: '',
		status: '',
	};

	const formSchema = Yup.object().shape({
		status: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<ResourcesUpdateStatus>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<ResourcesUpdateStatus> = async (data) => {
		if (
			(currentStatus == CommunityResourcesType.released ||
				currentStatus == CommunityResourcesType.removed) &&
			data.status == CommunityResourcesType.draft
		) {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={true}
					title={t('common.setStatus')}
					titleProps={{ variant: 'h6' }}
					description={
						<>
							<Typography variant='body1'>
								{t('communityResources.tobeSetStatus')}
								<span style={{ color: 'black', fontWeight: 'bold' }}>
									{t('communityResources.noCancel') + '。'}
								</span>
							</Typography>
							<br />
							<Typography variant='body1'>
								{t('communityResources.isSetStatus')}
							</Typography>
						</>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.sure')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						onSubmitApi(data);
					}}
				/>
			));
		} else {
			onSubmitApi(data);
		}
	};

	const onSubmitApi = async (data: ResourcesUpdateStatus) => {
		const newData: any = {
			ids: selectedId,
			mark: data.mark,
			status: data.status,
		};
		const tipTitle =
			Number(data.status) == CommunityResourcesType.draft
				? t('snackbar.resourcesStatusToDraft')
				: Number(data.status) == CommunityResourcesType.directorApproval
				? t('snackbar.resourcesStatusToDirectorApproval')
				: Number(data.status) == CommunityResourcesType.supervisorApproval
				? t('snackbar.resourcesStatusToSupervisorApproval')
				: Number(data.status) == CommunityResourcesType.awaitingRelease
				? t('snackbar.resourcesStatusToAwaitingRelease')
				: Number(data.status) == CommunityResourcesType.released
				? t('snackbar.resourcesStatusToReleased')
				: Number(data.status) == CommunityResourcesType.removed
				? t('snackbar.resourcesStatusToRemoved')
				: '';
		const res = await updateResourcesStatus(newData);
		if ('data' in res) {
			enqueueSnackbar(tipTitle, {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			!setStatusButton && setSelectedId([]);
		}
	};

	return (
		<>
			{setStatusButton ? (
				<Button
					startIcon={<CreateOutlinedIcon />}
					onClick={() => setOpen(true)}
				/>
			) : (
				<Button
					variant='outlined'
					startIcon={<CreateOutlinedIcon />}
					disabled={selectedId.length === 0}
					onClick={() => setOpen(true)}
				>
					{t('button.settingStatus')}
					{selectedId.length > 0 && ` (${selectedId.length})`}
				</Button>
			)}

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('common.settingStatus')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppDropDownMenu
										disabledItem={currentStatus}
										control={methods.control}
										name='status'
										label={t('common.status')}
										options={resourcesStatusList}
										required
									/>
								</Grid>
								{userRoles.statusRemark && (
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='mark'
											label={t('communityResources.remark')}
										/>
									</Grid>
								)}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SettingStatusModal;
